import { Platform } from 'react-native'
import { createClient } from '@supabase/supabase-js'
import { logger } from '../helpers/logger.helper'
import { quietProcessLock } from './quietProcessLock.helper'
import {
  SUPABASE_ANON_KEY as generatedKeyRaw,
  SUPABASE_URL as generatedUrlRaw,
} from './supabase-config.generated'

/** CI: scripts/inject-supabase-env.js overwrites literals; local: usually empty + EXPO_PUBLIC_* in .env */
const generatedUrl = String(generatedUrlRaw ?? '').trim()
const generatedKey = String(generatedKeyRaw ?? '').trim()

const supabaseUrl = (
  (process.env.EXPO_PUBLIC_SUPABASE_URL ?? '').trim() || generatedUrl
)
const supabaseAnonKey = (
  (process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '').trim() || generatedKey
)

if (!supabaseUrl || !supabaseAnonKey) {
  if (process.env.NODE_ENV !== 'production') {
    logger.warn('Supabase URL or anon key missing. Set EXPO_PUBLIC_SUPABASE_URL and EXPO_PUBLIC_SUPABASE_ANON_KEY in .env')
  } else {
    // createClient throws "supabaseUrl is required" during Expo static export — surface a fixable message for Vercel Preview vs Production env scopes.
    throw new Error(
      'Missing Supabase URL or anon key. Production builds from GitHub Actions need repository secrets EXPO_PUBLIC_SUPABASE_URL and EXPO_PUBLIC_SUPABASE_ANON_KEY (inject-supabase-env runs before vercel build). Vercel Preview: set both under Project → Environment Variables for Preview. Plain Vercel production builds: set for Production scope.'
    )
  }
}

/** In-memory storage for Node/SSR (e.g. during Expo web build) where window is not defined. */
const memoryStorage = (() => {
  const map = new Map<string, string>()
  return {
    getItem: async (key: string): Promise<string | null> => map.get(key) ?? null,
    setItem: async (key: string, value: string): Promise<void> => {
      map.set(key, value)
    },
    removeItem: async (key: string): Promise<void> => {
      map.delete(key)
    },
  }
})()

function hasDomWindow(): boolean {
  return globalThis.window != null
}

type AuthStorage = {
  getItem: (key: string) => Promise<string | null>
  setItem: (key: string, value: string) => Promise<void>
  removeItem: (key: string) => Promise<void>
}

/** Browser session persistence for OAuth (hash) + refresh; AsyncStorage on web can desync from Supabase expectations. */
const webLocalStorageAuth: AuthStorage = {
  getItem: async (key: string) => {
    if (!hasDomWindow()) return null
    try {
      return window.localStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem: async (key: string, value: string) => {
    if (!hasDomWindow()) return
    try {
      window.localStorage.setItem(key, value)
    } catch {
      /* quota / private mode */
    }
  },
  removeItem: async (key: string) => {
    if (!hasDomWindow()) return
    try {
      window.localStorage.removeItem(key)
    } catch {
      /* ignore */
    }
  },
}

const nativeAsyncStorageAuth: AuthStorage = {
  getItem: async (key: string) => {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default
    return AsyncStorage.getItem(key)
  },
  setItem: async (key: string, value: string) => {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default
    return AsyncStorage.setItem(key, value)
  },
  removeItem: async (key: string) => {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default
    return AsyncStorage.removeItem(key)
  },
}

/**
 * Web: localStorage (+ memory during static export when `window` is missing).
 * Native: AsyncStorage. Avoids touching `window` during SSR/build.
 */
const safeStorage: AuthStorage =
  Platform.OS === 'web'
    ? {
        getItem: async (key: string) => {
          if (!hasDomWindow()) return memoryStorage.getItem(key)
          return webLocalStorageAuth.getItem(key)
        },
        setItem: async (key: string, value: string) => {
          if (!hasDomWindow()) return memoryStorage.setItem(key, value)
          return webLocalStorageAuth.setItem(key, value)
        },
        removeItem: async (key: string) => {
          if (!hasDomWindow()) return memoryStorage.removeItem(key)
          return webLocalStorageAuth.removeItem(key)
        },
      }
    : nativeAsyncStorageAuth

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: safeStorage,
    autoRefreshToken: true,
    persistSession: true,
    // Web: false — /oauth-native-callback and /reset-password establish sessions explicitly
    // (PKCE code and/or implicit hash) so tokens are never parsed twice (invalid_grant / races).
    detectSessionInUrl: Platform.OS === 'web' ? false : hasDomWindow(),
    // In-memory lock instead of the default navigatorLock. Mobile Safari/Chrome freeze the page
    // (bfcache) during the OAuth redirect, leaving navigator.locks held by an operation that never
    // settles — the next auth call then aborts with "signal is aborted without reason" (instant on
    // retry). processLock can't get stuck this way. Tradeoff: no cross-tab refresh coordination.
    // quietProcessLock = same serialization as gotrue's processLock, minus the console flood it
    // emits on the benign 0ms auto-refresh-tick backoff (see quietProcessLock.helper.ts).
    lock: quietProcessLock,
  },
})
